import type { RangeTuple } from 'fuse.js';
import { isEmpty } from 'lodash-es';
import type { ReactNode } from 'react';
import { SafeImage } from '@/components/common/safe-image';
import HighlightSearchRange from '@/components/search/highlight-search-range';
import cn from '@/utils/merge-class-names';

interface Props {
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  avatarImageUrl: string;
  title: string;
  highlight?: string | readonly RangeTuple[] | null | undefined;
  highlightField?: 'title' | 'description' | 'suffix';
  description?: ReactNode;
  suffix?: ReactNode;
  onClick?: () => void;
}

export default function SearchListItem({
  className,
  avatarImageUrl,
  title,
  titleClassName,
  description,
  descriptionClassName,
  suffix,
  highlight,
  highlightField = 'title',
  onClick,
}: Props) {
  return (
    <div
      className={cn('flex w-full items-center px-5 py-2', className)}
      onClick={onClick}>
      {avatarImageUrl && (
        <SafeImage
          src={avatarImageUrl}
          className='size-10 shrink-0 rounded-full bg-cover'
          size={[40, 40]}
        />
      )}
      <div className='mx-4 flex flex-auto grow flex-col gap-1 overflow-hidden'>
        <h6 className={cn(' text-sm font-semibold', titleClassName)}>
          {highlightField === 'title' ? (
            <HighlightKeyword
              text={title}
              highlight={highlight}
            />
          ) : (
            title
          )}
        </h6>
        {description && (
          <p className={cn('truncate text-xs text-base-content/50', descriptionClassName)}>
            {highlightField === 'description' && typeof description === 'string' ? (
              <HighlightKeyword
                text={description}
                highlight={highlight}
              />
            ) : (
              description
            )}
          </p>
        )}
      </div>
      {suffix && typeof suffix === 'string' ? (
        <div className='whitespace-nowrap text-sm'>
          {highlightField === 'suffix' ? (
            <HighlightKeyword
              text={suffix}
              highlight={highlight}
            />
          ) : (
            suffix
          )}
        </div>
      ) : (
        suffix
      )}
    </div>
  );
}

export function HighlightKeyword({ text, highlight }: Pick<Props, 'highlight'> & { text: string }) {
  if (!highlight || isEmpty(highlight)) return text;

  if (typeof highlight === 'string') {
    const escapedKeyword = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedKeyword, 'ig');

    if (text.match(regex) === null) return text;
    const { index = 0 } = regex.exec(text) ?? {};
    const ranges: RangeTuple[] = [[index, index + highlight.length - 1]];

    return (
      <HighlightSearchRange
        ranges={ranges}
        text={text}
        className='text-primary'
      />
    );
  }

  if (Array.isArray(highlight)) {
    return (
      <HighlightSearchRange
        className='text-primary'
        text={text}
        ranges={highlight}
      />
    );
  }
}
